import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
    Container,
    Row,
    Col
} from "reactstrap";

import { RiBookOpenLine } from "react-icons/ri";

import Api from "../../components/helpers/Api";
import { SeoHelper } from "../../components/helpers/SeoHelper";
import { buildScriptureSEO } from "../../utils/buildScriptureSEO";

export default function ScriptureBookPage() {

    const { book } = useParams();
    const [bookMeta, setBookMeta] = useState(null);

    // ==========================
    // SEO (DETERMINISTIC)
    // ==========================
    const seo = buildScriptureSEO({
        book,
        chapter: null,
        verseStart: null,
        verseEnd: null
    });

    // ==========================
    // DATA LOAD (UI ONLY)
    // ==========================
    useEffect(() => {
        Api.get("/api/testament/books")
            .then(res => {
                const found = res.data.find(b => b.slug === book);
                setBookMeta(found);
            })
            .catch(err => console.error("Failed to load book metadata", err));
    }, [book]);

    // ==========================
    // ANALYTICS
    // ==========================
    useEffect(() => {
        if (typeof window.gtag === 'function') {
            window.gtag('event', 'view_scripture', {
                scripture_reference: formatBookName(book),
                source: 'browse'
            });
        }
    }, [book]);

    function formatBookName(value) {
        if (!value) return "";

        return value
            .replace(/-/g, " ")
            .split(" ")
            .filter(Boolean)
            .map(p => /^\d+$/.test(p) ? p : p.charAt(0).toUpperCase() + p.slice(1))
            .join(" ");
    }

    return (
        <>
            {/* ==========================
                SEO (ALWAYS RENDERED)
               ========================== */}
            <SeoHelper
                uri={seo.canonical}
                title={seo.title}
                description={seo.description}
            />

            {/* ==========================
                BREADCRUMB SCHEMA (DETERMINISTIC)
               ========================== */}
            <script type="application/ld+json">
                {JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "BreadcrumbList",
                    "itemListElement": [
                        {
                            "@type": "ListItem",
                            "position": 1,
                            "name": "Worship Songs",
                            "item": "https://theworshipbible.church/songs"
                        },
                        {
                            "@type": "ListItem",
                            "position": 2,
                            "name": seo.heading,
                            "item": `https://theworshipbible.church${seo.canonical}`
                        }
                    ]
                })}
            </script>

            {/* ==========================
                CONTENT
               ========================== */}
            {!bookMeta ? (
                <div className="text-center mt-5">Loading...</div>
            ) : (
                <section className="section section-lg">

                    <Container>

                        <Row className="justify-content-center">

                            <Col lg="8">

                                <h1 className="display-3 text-center text-primary">
                                    {seo.heading}
                                </h1>

                                <p className="lead text-center text-muted mb-5">
                                    Explore passages from {bookMeta.title} and discover worship songs connected to each
                                    chapter.
                                </p>

                                <h2 className="h4 text-center mb-4">
                                    Browse Chapters in {bookMeta.title}
                                </h2>

                                <ul className="twb-grid">

                                    {Array.from({ length: bookMeta.chapters }, (_, i) => i + 1).map(ch => (

                                        <li key={ch}>

                                            <Link
                                                to={`/songs/${book}/${ch}`}
                                                className="twb-next-step"
                                                aria-label={`Worship songs for ${bookMeta.title} chapter ${ch}`}
                                            >

                                                <div className="twb-next-step-icon">
                                                    <RiBookOpenLine size={22} />
                                                </div>

                                                <div className="twb-next-step-text">
                                                    Chapter&nbsp;{ch}
                                                </div>

                                            </Link>

                                        </li>

                                    ))}

                                </ul>

                                <p className="text-muted mt-5">
                                    Many churches plan worship services around scripture readings.
                                    Browse chapters in {bookMeta.title} to find passages and discover
                                    worship songs that reflect the themes of each reading.
                                </p>

                            </Col>

                        </Row>

                    </Container>

                </section>
            )}
        </>
    );
}
